// Admin Tickets — read-only list view of customer / engineer feedback +
// general support tickets. Disputes have their own page at /app/disputes
// (filtered to ticket_kind='dispute'), so this list passes
// exclude_kind=dispute so the two views don't overlap.
//
// Phase-1: no reply thread. Admin can see subject / body / rating / opener
// / status / kind / created date in the detail dialog. Status/assign
// mutations exist on the API but are not wired here yet.

import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { VisibilityOutlined as ViewIcon } from "@mui/icons-material";
import { tokens } from "@findgeeks/ui-tokens";
import { formatDateHK } from "@findgeeks/shared";
import { ApiError, apiFetch } from "@/lib/api";
import { DataTable, type DataTableColumn } from "@/components/DataTable";
import { useToast } from "@/components/Toast";
import { usePagedQuery } from "@/hooks/usePagedQuery";

type TicketStatus = "open" | "in_progress" | "resolved" | "closed";

const STATUS_OPTIONS: Array<{ value: TicketStatus; label: string }> = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In progress" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
];

interface TicketRow {
  id: string;
  subject: string;
  body: string | null;
  rating: number | null;
  status: "open" | "in_progress" | "resolved" | "closed";
  ticket_kind: "general" | "dispute" | "feedback";
  related_job_id: string | null;
  opener_id: string;
  assigned_to: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
  opener: {
    id: string;
    display_name: string | null;
    email: string | null;
    phone: string | null;
  } | null;
}

const STATUS_TABS: Array<{ id: string; label: string }> = [
  { id: "", label: "All" },
  { id: "open", label: "Open" },
  { id: "in_progress", label: "In progress" },
  { id: "resolved", label: "Resolved" },
  { id: "closed", label: "Closed" },
];

function statusChip(status: string): { color: string; bg: string; label: string } {
  switch (status) {
    case "open":
      return { color: "#B45309", bg: "#FEF3C7", label: "Open" };
    case "in_progress":
      return { color: "#1D4ED8", bg: "#DBEAFE", label: "In progress" };
    case "resolved":
      return { color: "#15803D", bg: "#DCFCE7", label: "Resolved" };
    case "closed":
      return { color: "#6B7280", bg: "#F3F4F6", label: "Closed" };
    default:
      return { color: "#6B7280", bg: "#F3F4F6", label: status };
  }
}

function kindChip(kind: string): { color: string; bg: string; label: string } {
  switch (kind) {
    case "feedback":
      return { color: "#7C3AED", bg: "#EDE9FE", label: "Feedback" };
    case "general":
      return { color: "#0E7490", bg: "#CFFAFE", label: "Support" };
    default:
      return { color: "#6B7280", bg: "#F3F4F6", label: kind };
  }
}

export function TicketsList() {
  const { show, ToastEl } = useToast();
  const [statusTab, setStatusTab] = useState<string>("");
  const [filters, setFilters] = useState<Record<string, string>>({
    exclude_kind: "dispute",
  });
  const [viewRow, setViewRow] = useState<TicketRow | null>(null);

  const list = usePagedQuery<TicketRow>("tickets", {
    filters,
    initialPagination: { page: 0, pageSize: 10 },
  });

  const onStatusChanged = (next: TicketStatus): void => {
    // Optimistic local update on the open dialog so admin sees the new
    // state immediately; list also refetches in the background.
    setViewRow((cur) =>
      cur
        ? {
            ...cur,
            status: next,
            resolved_at:
              next === "resolved" && !cur.resolved_at
                ? new Date().toISOString()
                : cur.resolved_at,
          }
        : cur,
    );
    show("Status updated — opener notified.", "success");
    list.refetch();
  };

  const onTabChange = (_e: React.SyntheticEvent, value: string): void => {
    setStatusTab(value);
    setFilters(
      value
        ? { exclude_kind: "dispute", status: value }
        : { exclude_kind: "dispute" },
    );
    list.setPaginationModel({ ...list.paginationModel, page: 0 });
  };

  const columns: Array<DataTableColumn<TicketRow>> = [
    {
      field: "subject",
      headerName: "Subject",
      render: (r) => (
        <Stack spacing={0.25}>
          <Typography variant="body2" fontWeight={600}>
            {r.subject}
          </Typography>
          {r.opener?.display_name ? (
            <Typography variant="caption" color="text.secondary">
              {`From ${r.opener.display_name}${r.opener.email ? ` · ${r.opener.email}` : ""}`}
            </Typography>
          ) : null}
        </Stack>
      ),
    },
    {
      field: "kind",
      headerName: "Kind",
      width: 120,
      render: (r) => {
        const v = kindChip(r.ticket_kind);
        return (
          <Chip
            label={v.label}
            size="small"
            sx={{
              color: v.color,
              backgroundColor: v.bg,
              fontWeight: 700,
              fontSize: 12,
            }}
          />
        );
      },
    },
    {
      field: "rating",
      headerName: "Rating",
      width: 90,
      render: (r) =>
        r.rating ? (
          <Typography variant="body2" fontWeight={600}>
            {"★".repeat(r.rating)}
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary">
            —
          </Typography>
        ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 140,
      render: (r) => {
        const v = statusChip(r.status);
        return (
          <Chip
            label={v.label}
            size="small"
            sx={{
              color: v.color,
              backgroundColor: v.bg,
              fontWeight: 700,
              fontSize: 12,
            }}
          />
        );
      },
    },
    {
      field: "created_at",
      headerName: "Opened",
      width: 160,
      render: (r) => (
        <Typography variant="caption">{formatDateHK(r.created_at)}</Typography>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 110,
      render: (r) => (
        <Button
          size="small"
          variant="outlined"
          startIcon={<ViewIcon />}
          onClick={() => setViewRow(r)}
          sx={{ textTransform: "none" }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        Tickets
      </Typography>

      <Paper sx={{ p: 0, mb: 2, border: `1px solid ${tokens.colors.admin.border}` }}>
        <Tabs
          value={statusTab}
          onChange={onTabChange}
          variant="standard"
          sx={{ px: 2 }}
        >
          {STATUS_TABS.map((t) => (
            <Tab key={t.id || "all"} value={t.id} label={t.label} />
          ))}
        </Tabs>
      </Paper>

      <Paper sx={{ p: 2, border: `1px solid ${tokens.colors.admin.border}` }}>
        <DataTable<TicketRow>
          rows={list.data?.items ?? []}
          rowCount={list.data?.total ?? 0}
          columns={columns}
          loading={list.isLoading || list.isFetching}
          paginationModel={list.paginationModel}
          onPaginationModelChange={list.setPaginationModel}
          getRowId={(r) => r.id}
        />
      </Paper>

      {viewRow ? (
        <ViewDialog
          ticket={viewRow}
          onClose={() => setViewRow(null)}
          onStatusChanged={onStatusChanged}
        />
      ) : null}
      {ToastEl}
    </Box>
  );
}

function ViewDialog({
  ticket,
  onClose,
  onStatusChanged,
}: {
  ticket: TicketRow;
  onClose: () => void;
  onStatusChanged: (next: TicketStatus) => void;
}): React.ReactElement {
  const status = statusChip(ticket.status);
  const kind = kindChip(ticket.ticket_kind);
  const [nextStatus, setNextStatus] = useState<TicketStatus>(ticket.status);
  const [statusError, setStatusError] = useState<string | null>(null);

  const patch = useMutation({
    mutationFn: (payload: { status: TicketStatus }) =>
      apiFetch(`/v1/admin/tickets/${ticket.id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      }),
    onSuccess: (_data, vars) => {
      setStatusError(null);
      onStatusChanged(vars.status);
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setStatusError(err.message || "Couldn't update status.");
      } else {
        setStatusError(err instanceof Error ? err.message : "Update failed.");
      }
    },
  });

  const onSaveStatus = (): void => {
    if (nextStatus === ticket.status || patch.isPending) return;
    setStatusError(null);
    patch.mutate({ status: nextStatus });
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{ticket.subject}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Stack direction="row" spacing={1}>
            <Chip
              label={status.label}
              size="small"
              sx={{
                color: status.color,
                backgroundColor: status.bg,
                fontWeight: 700,
                fontSize: 12,
              }}
            />
            <Chip
              label={kind.label}
              size="small"
              sx={{
                color: kind.color,
                backgroundColor: kind.bg,
                fontWeight: 700,
                fontSize: 12,
              }}
            />
            {ticket.rating ? (
              <Chip
                label={`${"★".repeat(ticket.rating)} (${ticket.rating}/5)`}
                size="small"
                variant="outlined"
              />
            ) : null}
          </Stack>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Change status
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              <TextField
                select
                size="small"
                value={nextStatus}
                onChange={(e) => setNextStatus(e.target.value as TicketStatus)}
                sx={{ minWidth: 180 }}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="contained"
                onClick={onSaveStatus}
                disabled={nextStatus === ticket.status || patch.isPending}
                sx={{ textTransform: "none" }}
              >
                {patch.isPending ? "Saving…" : "Save"}
              </Button>
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5, display: "block" }}
            >
              Opener gets a push + an in-app notification on save.
            </Typography>
            {statusError ? (
              <Typography variant="caption" sx={{ color: "#EF4444", mt: 0.5, display: "block" }}>
                {statusError}
              </Typography>
            ) : null}
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Opened by
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {ticket.opener?.display_name ?? "—"}
            </Typography>
            {ticket.opener?.email ? (
              <Typography variant="body2" sx={{ mt: 0.25 }}>
                <span style={{ color: "#6B7280" }}>Email: </span>
                <a
                  href={`mailto:${ticket.opener.email}`}
                  style={{ color: "#0E7490", textDecoration: "none" }}
                >
                  {ticket.opener.email}
                </a>
              </Typography>
            ) : null}
            {ticket.opener?.phone ? (
              <Typography variant="body2" sx={{ mt: 0.25 }}>
                <span style={{ color: "#6B7280" }}>Phone: </span>
                <a
                  href={`tel:${ticket.opener.phone}`}
                  style={{ color: "#0E7490", textDecoration: "none" }}
                >
                  {ticket.opener.phone}
                </a>
              </Typography>
            ) : null}
            {!ticket.opener?.email && !ticket.opener?.phone ? (
              <Typography variant="caption" color="text.secondary">
                No contact info on file.
              </Typography>
            ) : null}
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Opened
            </Typography>
            <Typography variant="body2">
              {formatDateHK(ticket.created_at)}
            </Typography>
          </Box>

          {ticket.body ? (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Message
              </Typography>
              <Typography
                variant="body2"
                sx={{ whiteSpace: "pre-wrap", color: "#1E3A6E", mt: 0.5 }}
              >
                {ticket.body}
              </Typography>
            </Box>
          ) : null}

          {ticket.related_job_id ? (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Related job
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                {ticket.related_job_id}
              </Typography>
            </Box>
          ) : null}

          {ticket.resolved_at ? (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Resolved
              </Typography>
              <Typography variant="body2">
                {formatDateHK(ticket.resolved_at)}
              </Typography>
            </Box>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
